<template>
  <h1>Create a Spellingframe account</h1>
  <div class="sm:tw-grid sm:tw-grid-cols-2 sm:tw-gap-8">
    <RegisterResume>
      <template v-slot:title> Single User Accounts </template>
      <template v-slot:default>
        <AccountShortDescription type="individual" />
      </template>
      <template v-slot:switch>
        <div v-if="mfTokenData">
          If you would prefer to register as a school please <ContactLink /> for
          further assistance.
        </div>
        <SfRouterLink v-else :to="'/register/school'">
          Switch to school account
        </SfRouterLink>
      </template>
    </RegisterResume>
    <div>
      <form ref="registerForm" @submit.prevent="submit">
        <fieldset :disabled="status == 'loaded'">
          <div>
            <h4 class="tw-font-bold">Parent Information</h4>
            <FormInput v-model="formData.fields.parentFirstName">
              <label> First Name* </label>
              <input
                type="text"
                v-model="formData.fields.parentFirstName.value"
                class="sf-form-control tw-w-full"
              />
            </FormInput>
            <FormInput v-model="formData.fields.parentLastName">
              <label> Last Name* </label>
              <input
                type="text"
                v-model="formData.fields.parentLastName.value"
                class="sf-form-control tw-w-full"
              />
            </FormInput>
            <FormInput
              v-model="formData.fields.parentEmail"
              :prevent-error-wrap="false"
              :custom-errors="{
                exists: 'A school user with this email already exists. Please use a different email address or contact us if you need to register with this email address.',
              }"
            >
              <label> Email address* </label>
              <div v-if="mfTokenData">
                {{ formData.fields.parentEmail.value }}
              </div>
              <input
                v-else
                type="text"
                v-model="formData.fields.parentEmail.value"
                class="sf-form-control tw-w-full"
              />
            </FormInput>
            <FormInput
              v-if="!mfTokenData"
              v-model="formData.fields.parentEmailConfirm"
            >
              <label> Confirm email address* </label>
              <input
                type="text"
                v-model="formData.fields.parentEmailConfirm.value"
                class="sf-form-control tw-w-full"
              />
            </FormInput>
          </div>
          <div>
            <h4 class="tw-font-bold">Student Information</h4>
            <FormInput v-model="formData.fields.first_name">
              <label> First Name* </label>
              <input
                type="text"
                v-model="formData.fields.first_name.value"
                class="sf-form-control tw-w-full"
              />
            </FormInput>
            <FormInput v-model="formData.fields.last_name">
              <label> Last Name* </label>
              <input
                type="text"
                v-model="formData.fields.last_name.value"
                class="sf-form-control tw-w-full"
              />
            </FormInput>
          </div>
          <FormInput
            v-if="isMathsAvailable"
            v-model="formData.fields.products"
            :custom-errors="{
              minLength: 'You must select at least one subscription',
            }"
          >
            <label> Subscriptions </label>
            <IndividualPurchaseField
              v-model="formData.fields.products.value"
              class="sf-label-light"
            />
          </FormInput>
        </fieldset>
        <div class="tw-mb-4">
          I have read, understood, and agree to the
          <a href="/sitemedia/Spellingframe_Terms_and_Conditions_FULL_v1.pdf" target="sf_tos"
            >terms and conditions</a
          >
        </div>
        <div>
          <button
            type="submit"
            :disabled="status == 'loaded'"
            class="sf-btn-primary"
          >
            Register
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import submitForm from "@/func/form/submitForm";
import {
  LoadingStatus,
  SfFormData,
  SfSubscriptionKind,
} from "@/types/generalTypes";
import { defineComponent, PropType } from "vue";
import SfRouterLink from "@/components/shared/SfRouterLink.vue";
import FormInput from "../ui/FormInput.vue";
import RegisterResume from "./RegisterResume.vue";
import AccountShortDescription from "./AccountShortDescription.vue";
import ContactLink from "../ui/ContactLink.vue";
import { MfTokenData } from "@/types/mfTypes";
import IndividualPurchaseField from "../purchase/IndividualPurchaseField.vue";
import { isMathsAvailable, refreshUser } from "@/state/appState";

export default defineComponent({
  components: {
    SfRouterLink,
    FormInput,
    RegisterResume,
    AccountShortDescription,
    ContactLink,
    IndividualPurchaseField,
  },
  emits: [],
  props: {
    mfTokenData: { type: Object as PropType<MfTokenData | null> },
  },
  data() {
    return {
      status: "inited" as LoadingStatus,
      formData: {
        fields: {
          parentEmail: {
            value: "",
            required: true,
            isEmail: true,
          },
          parentEmailConfirm: {
            value: "",
            required: true,
            isEmail: true,
            match: "parentEmail",
          },
          parentFirstName: { value: "", required: true },
          parentLastName: { value: "", required: true },
          first_name: { value: "", required: true },
          last_name: { value: "", required: true },
          products: {
            value: ["spelling"] as SfSubscriptionKind[],
            minLength: 1,
          },
        },
      } as SfFormData,
      isMfUser: false,
    };
  },
  computed: {
    mfToken() {
      return this.$route.query.mft || "";
    },
    isMathsAvailable() {
      return isMathsAvailable('individual', 'store');
    }
  },
  mounted() {
    if (this.mfTokenData && this.mfTokenData.userData) {
      this.formData.fields.first_name.value =
        this.mfTokenData.userData.first_name;
      this.formData.fields.last_name.value =
        this.mfTokenData.userData.last_name;
      this.formData.fields.parentLastName.value =
        this.mfTokenData.userData.last_name;
      this.formData.fields.parentEmail.value = this.mfTokenData.userData.email;
      this.formData.fields.parentEmailConfirm.value =
        this.mfTokenData.userData.email;
      this.isMfUser = true;
    }
  },
  methods: {
    async submit() {
      if (this.status == "loaded") {
        return;
      }
      this.status = "loading";
      const extraFields = {
        time_offset: new Date().getTimezoneOffset(),
        mfToken: this.mfToken,
      };
      const resData = await submitForm(
        "register/individual",
        this.formData,
        null,
        { extraFields },
      );
      this.formData = resData.formData;
      if (resData.response.success && resData.response.data?.success) {
        this.status = "loaded";
        refreshUser();
        if (this.isMfUser) {
          this.$router.replace("/dashboard");
        } else {
          const query = { 
            products: this.formData.fields.products.value, 
            userId: resData.response.data.individualId,
          };
          this.$router.replace({ path: "/purchase/individual/confirm", query });
        }
      } else {
        this.status = "error";
        this.$nextTick(this.scrollToError);
      }
    },
    scrollToError() {
      const form = this.$refs.registerForm as HTMLElement
      const elements = form.getElementsByClassName('has-error');
      if(elements.length > 0) {
        elements[0].scrollIntoView({ behavior: 'smooth', block: 'start'});
      }
    }
  },
});
</script>
